import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Facebook, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SERVICE_OPTIONS } from "@/data/site";

export const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    projectType: "",
    message: "",
  });

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }

    const lines = [
      `*New Quote Request — CWood Construction*`,
      ``,
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      form.email ? `Email: ${form.email}` : null,
      form.projectType ? `Project Type: ${form.projectType}` : null,
      form.message ? `Message: ${form.message}` : null,
    ].filter(Boolean);

    const text = encodeURIComponent(lines.join("\n"));
    const url = `https://wa.me/${CONTACT.phoneWa}?text=${text}`;
    window.open(url, "_blank");
    toast.success("Opening WhatsApp to send your request…");
  };

  const infoItems = [
    { icon: Phone, label: "Call", value: CONTACT.phoneDisplay, href: `tel:${CONTACT.phoneTel}` },
    { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: MapPin, label: "Location", value: CONTACT.location, href: null },
    { icon: Facebook, label: "Facebook", value: CONTACT.facebook, href: CONTACT.facebookUrl },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32 bg-[#F8FAFC]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm text-[#1656b8] uppercase tracking-[0.2em] font-semibold">
              Get In Touch
            </span>
            <h2 className="font-heading text-3xl sm:text-5xl font-bold text-slate-900 tracking-tight mt-3">
              Get Your Free Quote
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed max-w-md">
              Tell us about your project and we'll get back to you with a free,
              no-obligation quote. Fill in the form and it will send straight to us on
              WhatsApp — quick and easy.
            </p>

            <div className="mt-10 space-y-4">
              {infoItems.map((item, i) => {
                const Icon = item.icon;
                const content = (
                  <div className="flex items-center gap-4 bg-white border border-slate-200 rounded-xl p-4 hover:border-[#1656b8]/40 transition-colors">
                    <span className="w-11 h-11 rounded-lg bg-[#E8F0FE] flex items-center justify-center flex-shrink-0">
                      <Icon size={20} className="text-[#1656b8]" />
                    </span>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
                        {item.label}
                      </div>
                      <div className="text-slate-900 font-medium">{item.value}</div>
                    </div>
                  </div>
                );
                return item.href ? (
                  <a
                    key={i}
                    href={item.href}
                    target={item.label === "Facebook" ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={`contact-info-${item.label.toLowerCase()}`}
                    className="block"
                  >
                    {content}
                  </a>
                ) : (
                  <div key={i} data-testid={`contact-info-${item.label.toLowerCase()}`}>
                    {content}
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8"
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Name *
                  </label>
                  <input
                    data-testid="form-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your full name"
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-[#1656b8] focus:ring-2 focus:ring-[#1656b8]/20 transition-all outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    data-testid="form-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="07000 000000"
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-[#1656b8] focus:ring-2 focus:ring-[#1656b8]/20 transition-all outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email
                </label>
                <input
                  data-testid="form-email"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-[#1656b8] focus:ring-2 focus:ring-[#1656b8]/20 transition-all outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Project Type
                </label>
                <select
                  data-testid="form-project-type"
                  value={form.projectType}
                  onChange={update("projectType")}
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-[#1656b8] focus:ring-2 focus:ring-[#1656b8]/20 transition-all outline-none appearance-none"
                >
                  <option value="">Select a project type</option>
                  {SERVICE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Message
                </label>
                <textarea
                  data-testid="form-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={4}
                  placeholder="Tell us about your project…"
                  className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-slate-900 focus:border-[#1656b8] focus:ring-2 focus:ring-[#1656b8]/20 transition-all outline-none resize-none"
                />
              </div>

              <button
                data-testid="quote-form-submit"
                type="submit"
                className="w-full bg-[#1656b8] text-white rounded-lg px-8 py-4 font-semibold text-lg flex items-center justify-center gap-2 hover:bg-[#0f3d83] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              >
                <MessageCircle size={20} />
                Request Quote via WhatsApp
              </button>
              <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1">
                <Send size={12} /> Sends directly to {CONTACT.phoneDisplay}
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
